import React, { useState, useMemo, useEffect, useCallback } from 'react';
import './checkStock.css';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { exportToPDF } from './poExportPdf';
import LZString from 'lz-string';
import POSummary from './po-summary';
import POSummary2 from './po-summary2';

const API_BASE_URL = process.env.NODE_ENV === 'development' 
    ? 'http://127.0.0.1:8080'
    : 'https://bymolive-backend-995926394015.us-central1.run.app';

const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

const calculateSalesStats = (orders, salesKey, currentStock) => {
    let totalSales = 0;
    
    try {
        // First decompress the orders data if it's compressed
        const decompressedOrders = typeof orders === 'string' 
            ? JSON.parse(LZString.decompress(orders))
            : orders;

        if (Array.isArray(decompressedOrders)) {
            decompressedOrders.forEach(order => {
                if (order.line_items) {
                    order.line_items.forEach(item => {
                        const itemKey = `${item.product_id}-${item.variant_id}`;
                        if (itemKey === salesKey) {
                            totalSales += parseInt(item.quantity) || 0;
                        }
                    });
                }
            });
        }
    } catch (error) {
        console.error('Error processing orders:', error);
    }

    return {
        totalSales
    };
};

const getStatusColor = (duration) => {
    if (!duration) return '#999';
    if (duration > 60) return '#4CAF50';
    if (duration > 30) return '#FF9800';
    return '#f44336';
};

const StatusDot = ({ duration }) => (
    <span className="status-dot" style={{ backgroundColor: getStatusColor(duration) }} />
);

const calculateStockDuration = (currentStock, monthlySales) => {
    const dailySales = monthlySales / 30;
    return dailySales > 0 ? Math.round(currentStock / dailySales) : 180;
};

const getStockColor = (stockDuration) => {
    if (stockDuration >= 45) return '#4CAF50'; // green
    if (stockDuration >= 30) return '#FFA726'; // orange
    return '#EF5350'; // red
};

const PO = ({ inventory, coreProducts, variantSales, orders }) => {
    const [processedSales, setProcessedSales] = useState({});
    const [selectedVariants, setSelectedVariants] = useState([]);
    const [totalNewStockState, setTotalNewStock] = useState(0);
    const [saveStatus, setSaveStatus] = useState('');
    const [loadStatus, setLoadStatus] = useState('');
    const [exportType, setExportType] = useState('po');

    // Filter inventory to only show core products
    const filteredInventory = useMemo(() => {
        if (!coreProducts || !inventory) return [];
        
        // Get the list of core product IDs from coreProducts object
        const coreProductIds = Object.keys(coreProducts);
        
        // Filter inventory to only include core products
        return inventory.filter(product => 
            coreProductIds.includes(product.product_id.toString())
        );
    }, [inventory, coreProducts]);

    // Add POInputCell component inside PO
    const POInputCell = ({ variant, inputKey = 'po' }) => {
        const storageKey = `${inputKey}-stock-input-${variant.id}`;
        const [value, setValue] = useState(() => {
            // Check sessionStorage first, then localStorage as fallback
            const savedSession = sessionStorage.getItem(storageKey);
            const savedLocal = localStorage.getItem(storageKey);
            return savedSession ? parseInt(savedSession) : (savedLocal ? parseInt(savedLocal) : '');
        });

        const handleChange = (e) => {
            const newValue = e.target.value === '' ? '' : parseInt(e.target.value) || 0;
            setValue(newValue);
            localStorage.setItem(storageKey, newValue.toString());
            // Also save to sessionStorage
            sessionStorage.setItem(storageKey, newValue.toString());
        };

        return (
            <input
                type="number"
                min="0"
                value={value}
                onChange={handleChange}
                className="po-input"
                data-variant-id={variant.id}
                data-variant-title={variant.title}
                data-input-key={inputKey}
                placeholder="0"
            />
        );
    };

    // Initialize selectedVariants if empty
    useEffect(() => {
        if (inventory.length > 0 && selectedVariants.length === 0) {
            // Find the most common variant titles across all products
            const variantTitles = {};
            inventory.forEach(product => {
                product.variants.forEach(variant => {
                    if (!variantTitles[variant.title]) {
                        variantTitles[variant.title] = 0;
                    }
                    variantTitles[variant.title]++;
                });
            });
            
            // Sort by frequency and take the top ones
            const sortedVariants = Object.keys(variantTitles).sort(
                (a, b) => variantTitles[b] - variantTitles[a]
            );
            
            setSelectedVariants(sortedVariants.slice(0, 5)); // Take top 5 variants
        }
    }, [inventory, selectedVariants.length]);

    // Move StockAndSalesCell inside PO
    const StockAndSalesCell = ({ variant }) => {
        const salesKey = `${variant.product_id}-${variant.id}`;
        const salesData = processedSales[salesKey] || { totalSales: 0, last30DaysSales: 0 };
        
        const stockDuration = calculateStockDuration(variant.inventory_quantity, salesData.last30DaysSales || 1);
        const stockColor = getStockColor(stockDuration);

        return (
            <div className="simple-stock-cell">
                <span className="stock-value" style={{ color: stockColor }}>
                    {variant.inventory_quantity}
                </span>
                <span className="separator">|</span>
                <span className="sales-value">{salesData.last30DaysSales}</span>
                <span className="separator">|</span>
                <span className="sales-value">{salesData.totalSales}</span>
            </div>
        );
    };

    // Move FinalStockCell inside PO
    const FinalStockCell = ({ variant }) => {
        const [finalStock, setFinalStock] = useState(variant.inventory_quantity);
        const salesKey = `${variant.product_id}-${variant.id}`;
        const salesData = processedSales[salesKey] || { totalSales: 0, last30DaysSales: 0 };

        const handleRefresh = useCallback(() => {
            // Get the PO new value from localStorage
            const poValue = parseInt(localStorage.getItem(`po-stock-input-${variant.id}`)) || 0;
            // Calculate new final stock
            const newFinalStock = variant.inventory_quantity + poValue;
            // Update state
            setFinalStock(newFinalStock);
        }, [variant.inventory_quantity, variant.id, variant.title]);

        // Run handleRefresh on mount and when inventory changes
        useEffect(() => {
            handleRefresh();
        }, [handleRefresh]);

        // Listen for refresh events
        useEffect(() => {
            window.addEventListener('refreshFinalStock', handleRefresh);
            return () => window.removeEventListener('refreshFinalStock', handleRefresh);
        }, [handleRefresh]);

        const stockDuration = calculateStockDuration(finalStock, salesData.last30DaysSales || 1);
        const stockColor = getStockColor(stockDuration);

        return (
            <div className="simple-stock-cell">
                <span className="stock-value" style={{ color: stockColor }}>
                    {finalStock}
                </span>
                <span className="separator">|</span>
                <span className="sales-value">{salesData.last30DaysSales}</span>
                <span className="separator">|</span>
                <span className="sales-value">{salesData.totalSales}</span>
            </div>
        );
    };

    // Add calculateColumnTotals function
    const calculateColumnTotals = () => {
        let totalNewStock = 0;
        inventory.forEach(product => {
            selectedVariants.forEach(variantTitle => {
                const variant = product.variants.find(v => v.title === variantTitle);
                if (variant) {
                    const savedValue = localStorage.getItem(`po-stock-input-${variant.id}`);
                    totalNewStock += parseInt(savedValue || 0);
                }
            });
        });
        setTotalNewStock(totalNewStock);
    };

    // Process orders data once when component mounts
    useEffect(() => {
        try {
            const decompressedOrders = typeof orders === 'string' 
                ? JSON.parse(LZString.decompress(orders))
                : orders;

            const now = new Date();
            const thirtyDaysAgo = new Date(now.setDate(now.getDate() - 30));
            
            const salesMap = {};
            
            if (Array.isArray(decompressedOrders)) {
                decompressedOrders.forEach(order => {
                    if (order.line_items) {
                        order.line_items.forEach(item => {
                            const itemKey = `${item.product_id}-${item.variant_id}`;
                            if (!salesMap[itemKey]) {
                                salesMap[itemKey] = {
                                    totalSales: 0,
                                    last30DaysSales: 0
                                };
                            }
                            
                            const quantity = parseInt(item.quantity) || 0;
                            salesMap[itemKey].totalSales += quantity;
                            
                            if (new Date(order.created_at) >= thirtyDaysAgo) {
                                salesMap[itemKey].last30DaysSales += quantity;
                            }
                        });
                    }
                });
            }
            
            setProcessedSales(salesMap);
        } catch (error) {
            console.error('Error processing orders:', error);
        }
    }, [orders]);

    // Handle refresh button click
    const handleRefreshClick = () => {
        console.log("Refresh button clicked");
        // Create and dispatch a custom event
        const refreshEvent = new Event('refreshFinalStock');
        window.dispatchEvent(refreshEvent);
        
        // Calculate column totals
        calculateColumnTotals();
        setSaveStatus('');
    };

    // Handle save button click
    const handleSaveClick = () => {
        try {
            // Save all input values from localStorage to sessionStorage
            const inputKeys = ['po', 'po2'];
            
            inputKeys.forEach(inputKey => {
                // Get all keys from localStorage that match our pattern
                const keys = Object.keys(localStorage).filter(key => 
                    key.startsWith(`${inputKey}-stock-input-`)
                );
                
                // Copy values to sessionStorage
                keys.forEach(key => {
                    const value = localStorage.getItem(key);
                    sessionStorage.setItem(key, value);
                });
            });
            
            setSaveStatus('Data saved successfully!');
            setTimeout(() => setSaveStatus(''), 3000);
        } catch (error) {
            console.error('Error saving data:', error);
            setSaveStatus('Error saving data');
        }
    };

    // Load saved data on component mount
    useEffect(() => {
        try {
            // Check if we have saved data in sessionStorage
            const sessionKeys = Object.keys(sessionStorage).filter(key => 
                key.startsWith('po-stock-input-') || key.startsWith('po2-stock-input-')
            );
            
            if (sessionKeys.length > 0) {
                // Copy values to localStorage
                sessionKeys.forEach(key => {
                    const value = sessionStorage.getItem(key);
                    localStorage.setItem(key, value);
                });
                
                setLoadStatus('Saved data loaded!');
                setTimeout(() => setLoadStatus(''), 3000);
            }
        } catch (error) {
            console.error('Error loading saved data:', error);
        }
    }, []);

    // Handle clear PO new button click
    const handleClearPO = () => {
        const keys = Object.keys(localStorage).filter(key => 
            key.startsWith('po-stock-input-')
        );
        keys.forEach(key => {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
        });
        // Trigger refresh to update the display
        handleRefreshClick();
    };

    // Handle clear PO new2 button click
    const handleClearPO2 = () => {
        const keys = Object.keys(localStorage).filter(key => 
            key.startsWith('po2-stock-input-')
        );
        keys.forEach(key => {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
        });
        // Trigger refresh to update the display
        handleRefreshClick();
    };

    return (
        <div className="po-container">
            <POSummary inventory={inventory} coreProducts={coreProducts} />
            <POSummary2 inventory={inventory} coreProducts={coreProducts} />

            <div className="check-stock-container">
                <div className="stock-container">
                    <div className="po-header">
                        <h2>Purchase Order</h2>
                        <div className="po-actions">
                            <button 
                                onClick={handleRefreshClick}
                                className="refresh-all-button"
                                title="Refresh all calculations"
                            >
                                Refresh Data
                            </button>
                            <button 
                                onClick={handleClearPO}
                                className="clear-po"
                                title="Clear all PO new values"
                            >
                                Clear PO New
                            </button>
                            <button 
                                onClick={handleClearPO2}
                                className="clear-po2"
                                title="Clear all PO new2 values"
                            >
                                Clear PO New2
                            </button>
                            <button 
                                onClick={handleSaveClick}
                                className="save-button"
                                title="Save input values"
                            >
                                Save Data
                            </button>
                            <div className="export-container">
                                <select 
                                    value={exportType}
                                    onChange={(e) => setExportType(e.target.value)}
                                    className="export-select"
                                >
                                    <option value="po">PO New</option>
                                    <option value="po2">PO New2</option>
                                </select>
                                <button 
                                    onClick={() => exportToPDF(filteredInventory, selectedVariants, coreProducts, exportType === 'po2')}
                                    className="export-button"
                                    title="Export to PDF"
                                >
                                    Export PDF
                                </button>
                            </div>
                            {saveStatus && <span className="save-status">{saveStatus}</span>}
                            {loadStatus && <span className="load-status">{loadStatus}</span>}
                        </div>
                    </div>

                    <div className="inventory-table-container">
                        <table className="inventory-table">
                            <thead>
                                <tr>
                                    <th>Image</th>
                                    <th className="product-name-column">Product Name</th>
                                    {selectedVariants.map(variant => (
                                        <th key={variant}>Core {variant}</th>
                                    ))}
                                    {selectedVariants.map(variant => (
                                        <th key={`po-${variant}`}>PO new {variant}</th>
                                    ))}
                                    {selectedVariants.map(variant => (
                                        <th key={`final-${variant}`}>Final {variant}</th>
                                    ))}
                                    {selectedVariants.map(variant => (
                                        <th key={`po2-${variant}`}>PO new2 {variant}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredInventory.map((product) => (
                                    <tr key={product.product_id}>
                                        <td className="image-cell">
                                            {product.image_url ? (
                                                <img 
                                                    src={product.image_url} 
                                                    alt={product.product_title}
                                                    style={{width: '50px', height: '50px', objectFit: 'contain'}}
                                                />
                                            ) : (
                                                <div className="no-image">No Image</div>
                                            )}
                                        </td>
                                        <td className="product-name-cell">
                                            <div className="product-title">{product.product_title}</div>
                                            <div className="product-sku">
                                                {coreProducts[product.product_id]?.sku || product.sku || 'No SKU'}
                                            </div>
                                        </td>
                                        {/* Core columns */}
                                        {selectedVariants.map(variantTitle => {
                                            const variant = product.variants.find(v => v.title === variantTitle);
                                            return (
                                                <td key={variantTitle} className="variant-cell">
                                                    {variant ? (
                                                        <StockAndSalesCell 
                                                            variant={{...variant, product_id: product.product_id}}
                                                        />
                                                    ) : '-'}
                                                </td>
                                            );
                                        })}
                                        {/* PO new columns */}
                                        {selectedVariants.map(variantTitle => {
                                            const variant = product.variants.find(v => v.title === variantTitle);
                                            return (
                                                <td key={`po-${variantTitle}`} className="po-input-cell">
                                                    {variant ? (
                                                        <POInputCell 
                                                            variant={variant}
                                                            inputKey="po"
                                                        />
                                                    ) : '-'}
                                                </td>
                                            );
                                        })}
                                        {/* Final columns */}
                                        {selectedVariants.map(variantTitle => {
                                            const variant = product.variants.find(v => v.title === variantTitle);
                                            return (
                                                <td key={`final-${variantTitle}`} className="final-stock-cell">
                                                    {variant ? (
                                                        <FinalStockCell 
                                                            variant={{...variant, product_id: product.product_id}}
                                                        />
                                                    ) : '-'}
                                                </td>
                                            );
                                        })}
                                        {/* PO new2 columns */}
                                        {selectedVariants.map(variantTitle => {
                                            const variant = product.variants.find(v => v.title === variantTitle);
                                            return (
                                                <td key={`po2-${variantTitle}`} className="po-input-cell">
                                                    {variant ? (
                                                        <POInputCell 
                                                            variant={variant}
                                                            inputKey="po2"
                                                        />
                                                    ) : '-'}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PO; 